import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import { services } from "@/data/services";

export default function Services() {
  return (
    <>
      <Seo
        title="Services | Incremental Consulting"
        description="Programme & delivery management, PMO establishment & uplift, quality-assured delivery resourcing, SAFe training & certification, and independent delivery assurance reviews."
      />
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227]" data-testid="services-page-eyebrow">Services</p>
            <h1 className="mt-3 max-w-3xl text-balance font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="services-page-title">
              Senior delivery capability for complex, regulated change.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70">
              We lead the programme, stand up the PMO, resource and certify the team — and independently review delivery when you need a second opinion. Each line is something our clients already procure.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-7xl space-y-6 px-5 py-16 lg:px-8 lg:py-24">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={0.05}>
              <article
                className="grid gap-8 rounded-xl border border-[#0B1F3A]/10 bg-white p-8 lg:grid-cols-2 lg:p-10"
                data-testid={`services-block-${s.slug}`}
              >
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#A87B1F]">{s.category}</p>
                  <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#0B1F3A]">{s.title}</h2>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#172033]/85">{s.description}</p>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`services-detail-link-${s.slug}`}
                    className="group mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#A87B1F] hover:text-[#0B1F3A]"
                  >
                    Explore service
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
                <div>
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex gap-2.5 text-sm leading-relaxed text-[#172033]/85">
                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#A87B1F]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 rounded-lg bg-[#F5F8FC] px-4 py-3 text-[13.5px] leading-relaxed text-[#0B1F3A]" data-testid={`services-for-${s.slug}`}>
                    <span className="font-bold">For:</span> {s.forWhom}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand
        title="Tell us about the programme you need to land."
        text="We'll show you how we'd deliver it, resource it, or review whether it's on track."
      />
    </>
  );
}
